import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Eye, Shuffle, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { withNumber } from '../../utils/participantNumber';

/**
 * The trainer makes pairs by hand: tap two people to pair them, or tap one person and
 * then "Observe" on a pair to make them its observer. Anyone not placed is paired at
 * random on submit when "Pair everyone left at random" is ticked; otherwise they wait,
 * and the console can pair them later.
 *
 * Used before launch (LaunchMicroSimPanel) and on the console for people still waiting.
 * On the console an observer can also join a pair that is already playing (runningPairs).
 *
 * onSubmit receives the request the server expects:
 *   { pairs: [[idA, idB]], observers: [{ participantId, pairIndex } | { participantId, runId }],
 *     pairRestRandomly }
 * The server checks it again, so a person who left meanwhile is reported, not paired.
 */

const Chip = ({ person, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
      selected
        ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
        : 'border-neutral-200 bg-white text-neutral-800 hover:border-primary-300 hover:bg-primary-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
    }`}
  >
    {person.number && <span className={`font-bold tabular-nums ${selected ? 'text-white' : 'text-primary-700 dark:text-primary-300'}`}>#{person.number}</span>}
    <span>{person.name}</span>
  </button>
);

Chip.propTypes = {
  person: PropTypes.shape({ number: PropTypes.number, name: PropTypes.string }).isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const ObserverTag = ({ person, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 py-0.5 pl-2 pr-1 text-xs text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
    <Eye size={11} /> {withNumber(person.name, person.number)}
    <button type="button" onClick={onRemove} className="rounded-full p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700" aria-label={`Remove ${person.name} as observer`}>
      <X size={11} />
    </button>
  </span>
);

ObserverTag.propTypes = {
  person: PropTypes.shape({ number: PropTypes.number, name: PropTypes.string }).isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ManualPairingDialog = ({
  isOpen,
  onClose,
  people,
  runningPairs = [],
  onSubmit,
  submitting = false,
  submitLabel = 'Start with these pairs',
  title = 'Choose pairs',
}) => {
  const [pairs, setPairs] = useState([]); // [{ key, ids: [idA, idB] }]
  const [observers, setObservers] = useState([]); // [{ participantId, pairKey } | { participantId, runId }]
  const [selected, setSelected] = useState(null);
  const [restRandom, setRestRandom] = useState(false);
  const nextKey = useRef(1);

  // A fresh start each time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setPairs([]);
    setObservers([]);
    setSelected(null);
    setRestRandom(false);
  }, [isOpen]);

  const byId = useMemo(() => new Map(people.map((p) => [p.participantId, p])), [people]);

  // The room can change while the dialog is open (the console refreshes). Anyone who
  // left drops out of their pair; an observer whose pair broke goes back to waiting.
  const livePairs = pairs.filter((p) => p.ids.every((id) => byId.has(id)));
  const liveKeys = new Set(livePairs.map((p) => p.key));
  const runningIds = new Set(runningPairs.map((r) => r.runId));
  const liveObservers = observers.filter((o) => byId.has(o.participantId)
    && (o.pairKey ? liveKeys.has(o.pairKey) : runningIds.has(o.runId)));
  const placed = new Set([...livePairs.flatMap((p) => p.ids), ...liveObservers.map((o) => o.participantId)]);
  const waiting = people.filter((p) => !placed.has(p.participantId));
  const chosen = selected && byId.has(selected) && !placed.has(selected) ? selected : null;

  const tap = (id) => {
    if (chosen === id) { setSelected(null); return; }
    if (chosen) {
      setPairs((list) => [...list, { key: nextKey.current++, ids: [chosen, id] }]);
      setSelected(null);
      return;
    }
    setSelected(id);
  };

  const removePair = (key) => {
    setPairs((list) => list.filter((p) => p.key !== key));
    setObservers((list) => list.filter((o) => o.pairKey !== key));
  };

  const addObserver = (target) => {
    if (!chosen) return;
    setObservers((list) => [...list, { participantId: chosen, ...target }]);
    setSelected(null);
  };

  const removeObserver = (participantId) => setObservers((list) => list.filter((o) => o.participantId !== participantId));

  const observersOf = (match) => liveObservers.filter(match).map((o) => byId.get(o.participantId));

  const pairRest = restRandom && waiting.length > 0;
  const canSubmit = livePairs.length > 0 || liveObservers.length > 0
    || (pairRest && (waiting.length >= 2 || runningPairs.length > 0));

  const submit = () => {
    const index = new Map(livePairs.map((p, i) => [p.key, i]));
    onSubmit({
      pairs: livePairs.map((p) => p.ids),
      observers: liveObservers.map((o) => (o.pairKey
        ? { participantId: o.participantId, pairIndex: index.get(o.pairKey) }
        : { participantId: o.participantId, runId: o.runId })),
      pairRestRandomly: pairRest,
    });
  };

  let leftover = 'Everyone is placed.';
  if (waiting.length && pairRest) {
    leftover = `${waiting.length} left will be paired at random${waiting.length % 2 ? ' (the odd one out observes a pair)' : ''}.`;
  } else if (waiting.length) {
    leftover = `${waiting.length} not placed yet. They wait on the console until you pair them.`;
  }

  const observeButton = (target) => chosen && (
    <Button variant="secondary" size="sm" onClick={() => addObserver(target)}>
      <Eye size={13} className="mr-1" /> Observe
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={title}
      size="lg"
      footer={(
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-neutral-500">{leftover}</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button variant="primary-filled" onClick={submit} loading={submitting} disabled={!canSubmit || submitting}>
              {submitLabel}
            </Button>
          </div>
        </div>
      )}
    >
      <div className="space-y-5">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Tap two people to pair them. To add an observer, tap a person, then <span className="font-semibold">Observe</span> on a pair.
        </p>

        <section>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Waiting ({waiting.length})</p>
          {waiting.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {waiting.map((p) => (
                <Chip key={p.participantId} person={p} selected={chosen === p.participantId} onClick={() => tap(p.participantId)} />
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-neutral-400">{people.length ? 'Everyone is in a pair.' : 'Nobody is waiting to be paired.'}</p>
          )}
          {chosen && (
            <p className="mt-2 text-xs font-medium text-primary-700 dark:text-primary-300">
              {withNumber(byId.get(chosen).name, byId.get(chosen).number)} selected. Tap their partner, or Observe on a pair.
            </p>
          )}
        </section>

        {livePairs.length > 0 && (
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Your pairs ({livePairs.length})</p>
            <ul className="mt-2 space-y-2">
              {livePairs.map((pair, i) => {
                const [a, b] = pair.ids.map((id) => byId.get(id));
                const watching = observersOf((o) => o.pairKey === pair.key);
                return (
                  <li key={pair.key} className="rounded-xl border border-neutral-200 px-3.5 py-2.5 dark:border-neutral-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-neutral-400">Pair {i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {withNumber(a.name, a.number)} &amp; {withNumber(b.name, b.number)}
                      </span>
                      {observeButton({ pairKey: pair.key })}
                      <button type="button" onClick={() => removePair(pair.key)} className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" aria-label={`Break up pair ${i + 1}`}>
                        <X size={15} />
                      </button>
                    </div>
                    {watching.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {watching.map((p) => <ObserverTag key={p.participantId} person={p} onRemove={() => removeObserver(p.participantId)} />)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {runningPairs.length > 0 && (
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Already playing — add an observer</p>
            <ul className="mt-2 space-y-2">
              {runningPairs.map((run) => {
                const watching = observersOf((o) => o.runId === run.runId);
                return (
                  <li key={run.runId} className="rounded-xl bg-neutral-50 px-3.5 py-2.5 dark:bg-neutral-800/60">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-neutral-700 dark:text-neutral-300">{run.label}</span>
                      {observeButton({ runId: run.runId })}
                    </div>
                    {watching.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {watching.map((p) => <ObserverTag key={p.participantId} person={p} onRemove={() => removeObserver(p.participantId)} />)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-neutral-200 px-3.5 py-3 dark:border-neutral-700">
          <input
            type="checkbox"
            checked={restRandom}
            onChange={(e) => setRestRandom(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600"
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <Shuffle size={14} /> Pair everyone left at random
            </span>
            <span className="block text-xs text-neutral-500">Anyone you have not placed is paired at random when you start.</span>
          </span>
        </label>
      </div>
    </Modal>
  );
};

ManualPairingDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // [{ participantId, number, name }] — who can be placed
  people: PropTypes.arrayOf(PropTypes.shape({
    participantId: PropTypes.string.isRequired,
    number: PropTypes.number,
    name: PropTypes.string.isRequired,
  })).isRequired,
  // [{ runId, label }] — pairs already playing (console only)
  runningPairs: PropTypes.arrayOf(PropTypes.shape({ runId: PropTypes.string.isRequired, label: PropTypes.string.isRequired })),
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  submitLabel: PropTypes.string,
  title: PropTypes.string,
};

export default ManualPairingDialog;
